/**
 * State Manager - A minimal state management system
 * 
 * This module provides a centralized state store for managing application state
 * and component coordination. It replaces global variables and fragmented state
 * management with a predictable state container.
 */

/**
 * Creates a new state manager
 * @param {Object} initialState - Initial state object
 * @param {Object} options - Configuration options
 * @param {Object} options.eventBus - Event bus for state change events (defaults to window.eventBus)
 * @returns {Object} State manager instance
 */
function createStateManager(initialState = {}, options = {}) {
  // The state object (immutable between operations)
  let state = { ...initialState };
  
  // Get eventBus from options or fall back to window.eventBus or a noop implementation
  const eventBus = options.eventBus || 
                  (typeof window !== 'undefined' && window.eventBus) || 
                  { emit: () => {}, on: () => {}, off: () => {} };
  
  /**
   * Get the current state
   * @param {string} path - Optional dot notation path to specific state
   * @returns {any} Current state value
   */
  function getState(path) {
    if (!path) {
      return { ...state };
    }
    
    // Handle dot notation paths
    return path.split('.').reduce((obj, key) => {
      return obj && obj[key] !== undefined ? obj[key] : undefined;
    }, state);
  }
  
  /**
   * Update the state
   * @param {Object|string} update - Update object or path string
   * @param {any} value - Value if path is provided
   * @returns {Object} Updated state
   */
  function setState(update, value) {
    const prevState = { ...state };
    const changes = {};
    
    // Handle string path or object update
    if (typeof update === 'string') {
      // Set nested value using path
      const path = update.split('.');
      const lastKey = path.pop();
      let current = state;
      
      // Navigate to the right object
      const newState = { ...state };
      let currentNew = newState;
      
      path.forEach(key => {
        current = current[key] = current[key] || {};
        currentNew[key] = currentNew[key] ? { ...currentNew[key] } : {};
        currentNew = currentNew[key];
      });
      
      // Set the value
      currentNew[lastKey] = value;
      
      // Track change
      changes[update] = {
        oldValue: getState(update),
        newValue: value
      };
      
      // Update state
      state = newState;
    } else if (typeof update === 'object' && update !== null) {
      // Merge object update
      state = { ...state, ...update };
      
      // Track changes
      Object.keys(update).forEach(key => {
        changes[key] = {
          oldValue: prevState[key],
          newValue: update[key]
        };
      });
    } else {
      throw new Error('Invalid state update: must be an object or string path');
    }
    
    // Emit change events
    Object.keys(changes).forEach(key => {
      eventBus.emit(`state:${key}`, changes[key].newValue, changes[key].oldValue);
    });
    
    // Emit general change event
    eventBus.emit('state:changed', state, prevState, changes);
    
    return { ...state };
  }
  
  /**
   * Listen for changes to a specific part of state
   * @param {string} path - Dot notation path to state (or '*' for all changes)
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  function subscribe(path, callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }
    
    const eventName = path === '*' ? 'state:changed' : `state:${path}`;
    
    // Add listener using the event bus
    eventBus.on(eventName, callback);
    
    // Return unsubscribe function
    return function unsubscribe() {
      eventBus.off(eventName, callback);
    };
  }
  
  /**
   * Reset the state to initial value
   * @param {Object} newInitialState - Optional new initial state
   * @returns {Object} New state
   */
  function resetState(newInitialState = initialState) {
    const prevState = { ...state };
    state = { ...newInitialState };
    
    // Emit reset event
    eventBus.emit('state:reset', state, prevState);
    
    return { ...state };
  }
  
  // Return the public API
  return {
    getState,
    setState,
    subscribe,
    resetState
  };
}

// Create default instance with initial application state
const stateManager = createStateManager({
  ui: {
    loading: {}, // Loading states for different components
    errors: {}   // Error states for different components
  },
  data: {
    journeys: {},
    diagrams: {},
    engagementTips: {}
  }
});

// Export the state manager as ES modules
export { stateManager, createStateManager };
export default stateManager;

// Also make available globally for backward compatibility during transition
window.stateManager = stateManager;
window.createStateManager = createStateManager;